import {Repository} from "./Repository";
import {AxiosInstance, AxiosRequestConfig} from "axios";
import {ElementInterface} from "../models/interfaces/ElementInterface";
import { Element } from "../models/Element";
import {QueryParametersInterface} from "./interfaces/QueryParametersInterface";
import {ElementConverter} from "@api/repositories/types/ElementConverter";
import { ObjectFindParameters } from "@api/repositories/interfaces/ObjectFindParameters";

export class ObjectRepository implements Repository{
    client: AxiosInstance;

    constructor(client: AxiosInstance) {
        this.client = client;
    }

    applyConverter<T>(item: ElementInterface, schema: string, converter?: ElementConverter<T>) {
        if (converter) {
            return converter(item, schema);
        }

        return new Element(item, schema);
    }

    query<T extends Element[], C = ElementInterface>(schema: string, query: QueryParametersInterface = {}, converter?: ElementConverter<C>, config?: AxiosRequestConfig): Promise<T> {
        return this.client.post(`/objects/${schema}/query`, query, config)
            .then((obj) => {
                return (obj.data || []).map((item: ElementInterface) => this.applyConverter(item, schema, converter));
            });
    }

    queryWithCount<T extends Element[]>(schema: string, query: QueryParametersInterface = {}, converter?: ElementConverter, config?: AxiosRequestConfig): Promise<{ data: T; count: number }> {
        return this.client.post(`/objects/${schema}/query`, query, {
            ...config,
            params: { count: true }
        })
            .then(({data, headers}) => {
                return {
                    data: (data || []).map((item: ElementInterface) => this.applyConverter(item, schema, converter)),
                    count: parseInt(headers['x-appercode-totalitems'] || 0)
                };
            });
    }

    find<T extends Element>(parameters: ObjectFindParameters<T>): Promise<T> {
        return this.client
            .get<T>(`/objects/${parameters.schema}/${parameters.objectId}`, {
                headers: { "X-Appercode-Language": parameters.lang },
                params: parameters.include
                    ? { include: '[' + parameters.include.map(item => `'${item}'`).join(',') + ']' }
                    : {}
            })
            .then((obj) => {
                return this.applyConverter(obj.data, parameters.schema, parameters.converter) as T;
            });
    }

    getByLanguages<T extends Element>(parameters: ObjectFindParameters<T>, languages: string[]) {
        const promises = languages.map((lang, index) => {
                const languageParameters = { ...parameters, ...{ lang } };

                const needToFilterIncludeForSecondaryLanguages = index > 0 && parameters.schemaSettings?.hasLocalizableFields();
                if (needToFilterIncludeForSecondaryLanguages) {
                    languageParameters.include = languageParameters.include?.filter(item =>
                        parameters.schemaSettings?.isFieldLocalizable(item)
                    );
                }

                return this.find<T>(languageParameters)
                    .then((data) => {
                        return {[lang]: data}
                    });
            }
        );

        return Promise.all(promises)
            .then((data) => {
                return data.reduce((acc, current) => {
                    return { ...acc, ...current }
                }, {})
            })
    }

    count(query: QueryParametersInterface = {}, config = {}): Promise<number> {
        return this.client.post(`/objects/UserProfiles/query`, query, {
            ...config,
            params: { count: true, take: 0 }
        })
            .then(({ data, headers }) => {
                return parseInt(headers['x-appercode-totalitems'] || 0)
            })
    }

    create<T extends Element>(schema: string, data: any, converter?: ElementConverter) {
        return this.client.post(`/objects/${schema}`, data)
            .then(({data}) => {
                return this.applyConverter(data, schema, converter) as T;
            })
    }

    createBatch<T extends Element[]>(schema: string, data: any, converter?: ElementConverter) {
        return this.client.post<T>(`/objects/${schema}/batch`, data)
            .then(({ data }) => {
                return (data || []).map((item: ElementInterface) => this.applyConverter(item, schema, converter));
            })
    }

    delete(schema: string, ids: string[]) {
        return this.client.delete(`/objects/${schema}/batch`, {
            data: ids
        });
    }

    update<T>(schema: string, id: string, changes: any) {
        return this.client.put<T>(`/objects/${schema}/${id}`, changes)
    }

    /**
     * Updating object with lang fields
     * Example:
     * {
     *      fields: {
     *          "fieldA": "valueA"
     *      },
     *      "languages": {
     *          "en": {
     *              "fieldA": "valueA"
     *          }
     *      }
     * }
     */
    updateV2(schema: string, id: string, changes: any) {
        return this.client.put(`v2/objects/${schema}/${id}`, changes).then(({data}) => data)
    }
}
